/**
 * Admin Hazards moderation page.
 *
 * Surfaces the crowd-sourced `public.hazards` table for admin review.
 * Operators see every report (active, inactive, expired) with reporter
 * profile context and can:
 *
 *   • Force-deactivate a hazard (spam, fake, mis-placed pin).
 *   • Restore a hazard the admin or the 3-downvote trigger took down.
 *
 * Why a dedicated page (vs surfacing in Incidents):
 *   Hazards aren't user-on-user safety reports — they're road-state
 *   data points. Putting them on the Trust & Safety page would mix
 *   moderation workflows that have different SLAs (incidents = hours;
 *   hazards = self-clearing in < 6h). Separate page = separate filter
 *   chips, separate column model, separate operator headspace.
 *
 * Data path: `admin_list_hazards` / `admin_dismiss_hazard` /
 * `admin_restore_hazard` RPCs from `20260624010000_hazards_admin_moderation.sql`.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import { AlertTriangle, ChevronLeft, ChevronRight, RotateCcw, Ban, Search } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

interface AdminHazardRow {
  id: string;
  reporter_id: string | null;
  reporter_name: string | null;
  reporter_phone: string | null;
  corridor: string;
  hazard_type: string;
  latitude: number;
  longitude: number;
  upvotes: number;
  downvotes: number;
  is_active: boolean;
  reported_at: string;
  expires_at: string;
  route_origin: string | null;
  route_destination: string | null;
  ride_direction: string | null;
}

type StatusFilter = 'all' | 'active' | 'inactive' | 'expired';

const STATUS_LABEL: Record<StatusFilter, string> = {
  all: 'All',
  active: 'Active',
  inactive: 'Deactivated',
  expired: 'Expired',
};

const HAZARD_TYPES = ['Landslide', 'Flooding', 'Roadwork', 'Accident', 'Other'];

const PAGE_SIZE = 50;

function FilterChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
        active
          ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30'
          : 'bg-gray-100 dark:bg-gray-800/50 text-gray-600 dark:text-gray-400 border-gray-300 dark:border-gray-700 hover:border-gray-500'
      }`}
    >
      {label}
    </button>
  );
}

function statusBadge(row: AdminHazardRow) {
  const expired = new Date(row.expires_at).getTime() <= Date.now();
  if (!row.is_active) {
    return (
      <Badge variant="outline" className="border-gray-500/30 text-gray-500">
        Deactivated
      </Badge>
    );
  }
  if (expired) {
    return (
      <Badge variant="outline" className="border-amber-500/30 text-amber-600 dark:text-amber-400">
        Expired
      </Badge>
    );
  }
  return (
    <Badge className="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30 border">
      Active
    </Badge>
  );
}

export default function Hazards() {
  const [rows, setRows] = useState<AdminHazardRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [typeFilter, setTypeFilter] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.rpc('admin_list_hazards', {
        p_status: statusFilter === 'all' ? null : statusFilter,
        p_corridor: null,
        p_hazard_type: typeFilter,
        p_limit: PAGE_SIZE,
        p_offset: page * PAGE_SIZE,
      });

      if (error) throw error;
      setRows((data ?? []) as AdminHazardRow[]);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to load hazards';
      toast.error(msg);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, typeFilter, page]);

  useEffect(() => {
    void load();
  }, [load]);

  /*
   * Client-side fuzzy filter on top of the server-side filters above.
   * The server filters by status/type/corridor for index hits; this
   * lets the operator narrow further by reporter name or corridor
   * substring without forcing a round-trip on every keystroke.
   */
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.corridor.toLowerCase().includes(q) ||
        (r.reporter_name ?? '').toLowerCase().includes(q) ||
        (r.reporter_phone ?? '').toLowerCase().includes(q) ||
        r.hazard_type.toLowerCase().includes(q),
    );
  }, [rows, search]);

  const dismiss = useCallback(
    async (id: string) => {
      setBusyId(id);
      try {
        const { data, error } = await supabase.rpc('admin_dismiss_hazard', { p_id: id });
        if (error) throw error;
        const result = data as { ok: boolean; error?: string } | null;
        if (!result?.ok) throw new Error(result?.error ?? 'Failed to dismiss');
        toast.success('Hazard deactivated');
        await load();
      } catch (e) {
        const msg = e instanceof Error ? e.message : 'Failed to dismiss hazard';
        toast.error(msg);
      } finally {
        setBusyId(null);
      }
    },
    [load],
  );

  const restore = useCallback(
    async (id: string) => {
      setBusyId(id);
      try {
        const { data, error } = await supabase.rpc('admin_restore_hazard', { p_id: id });
        if (error) throw error;
        const result = data as { ok: boolean; error?: string } | null;
        if (!result?.ok) throw new Error(result?.error ?? 'Failed to restore');
        toast.success('Hazard restored with a fresh 6h TTL');
        await load();
      } catch (e) {
        const msg = e instanceof Error ? e.message : 'Failed to restore hazard';
        toast.error(msg);
      } finally {
        setBusyId(null);
      }
    },
    [load],
  );

  return (
    <div className="p-6 space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <AlertTriangle className="w-6 h-6 text-amber-500" />
            Road Hazards
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Crowd-sourced hazard reports from drivers and passengers. Hazards
            auto-expire after 6&nbsp;hours; deactivate spam or restore an
            over-cleared report.
          </p>
        </div>
        <Button variant="outline" onClick={() => void load()} disabled={loading}>
          Refresh
        </Button>
      </header>

      <Card>
        <CardContent className="p-4 space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-semibold text-gray-500 mr-1">Status:</span>
            {(Object.keys(STATUS_LABEL) as StatusFilter[]).map((s) => (
              <FilterChip
                key={s}
                label={STATUS_LABEL[s]}
                active={statusFilter === s}
                onClick={() => {
                  setPage(0);
                  setStatusFilter(s);
                }}
              />
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-semibold text-gray-500 mr-1">Type:</span>
            <FilterChip
              label="All"
              active={typeFilter === null}
              onClick={() => {
                setPage(0);
                setTypeFilter(null);
              }}
            />
            {HAZARD_TYPES.map((t) => (
              <FilterChip
                key={t}
                label={t}
                active={typeFilter === t}
                onClick={() => {
                  setPage(0);
                  setTypeFilter(t);
                }}
              />
            ))}
          </div>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              placeholder="Search corridor / reporter / type…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-4 space-y-2">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center text-gray-500">
              <AlertTriangle className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p>No hazards match these filters.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Status</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Corridor</TableHead>
                  <TableHead>Reporter</TableHead>
                  <TableHead>Votes</TableHead>
                  <TableHead>Reported</TableHead>
                  <TableHead>Expires</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((row) => {
                  const expired = new Date(row.expires_at).getTime() <= Date.now();
                  const canDismiss = row.is_active;
                  const canRestore = !row.is_active || expired;
                  return (
                    <TableRow key={row.id}>
                      <TableCell>{statusBadge(row)}</TableCell>
                      <TableCell className="font-medium">{row.hazard_type}</TableCell>
                      <TableCell>
                        <div className="text-sm">{row.corridor}</div>
                        {row.route_origin && row.route_destination ? (
                          <div className="text-xs text-gray-500">
                            {row.route_origin} → {row.route_destination}
                            {row.ride_direction ? ` · ${row.ride_direction}` : ''}
                          </div>
                        ) : null}
                        <div className="text-xs text-gray-400 font-mono">
                          {row.latitude.toFixed(4)}, {row.longitude.toFixed(4)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">{row.reporter_name ?? '–'}</div>
                        <div className="text-xs text-gray-500">{row.reporter_phone ?? ''}</div>
                      </TableCell>
                      <TableCell>
                        <span className="text-emerald-600 dark:text-emerald-400 font-semibold">
                          ▲ {row.upvotes}
                        </span>{' '}
                        <span className="text-red-500 font-semibold">▼ {row.downvotes}</span>
                      </TableCell>
                      <TableCell className="text-sm text-gray-500">
                        {formatDistanceToNow(new Date(row.reported_at), { addSuffix: true })}
                      </TableCell>
                      <TableCell className="text-sm text-gray-500">
                        {expired
                          ? 'expired'
                          : formatDistanceToNow(new Date(row.expires_at), { addSuffix: true })}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          {canDismiss ? (
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => void dismiss(row.id)}
                              disabled={busyId === row.id}
                            >
                              <Ban className="w-3.5 h-3.5 mr-1" />
                              Deactivate
                            </Button>
                          ) : null}
                          {canRestore ? (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => void restore(row.id)}
                              disabled={busyId === row.id}
                            >
                              <RotateCcw className="w-3.5 h-3.5 mr-1" />
                              Restore
                            </Button>
                          ) : null}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">
          Page {page + 1} · {filtered.length} row{filtered.length === 1 ? '' : 's'}
        </p>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0 || loading}
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => p + 1)}
            disabled={loading || rows.length < PAGE_SIZE}
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
